import { useParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Contact from "../components/Contact";
import CuratedReviews from "../components/CuratedReviews";
import { SEO, breadcrumbSchema, localBusinessSchema } from "../components/SEO";
import { BRAND } from "../mock";
import { BLOG_POSTS } from "../blog";
import { ArrowLeft, Clock, Calendar } from "lucide-react";

// Article bodies (kept here so the blog stays self-contained without a CMS)
const BODIES = {
  "ceramic-coating-cost-mississauga-2026": (
    <>
      <p>
        If you've shopped around for <strong>ceramic coating in Mississauga</strong>{" "}
        in 2026, you've probably seen prices range from <em>"$199 special!"</em> all
        the way up to $3,000+ for premium graphene packages. So what should you
        actually pay. and more importantly, what are you actually getting?
      </p>
      <p>
        After applying coatings to 1,500+ cars across Mississauga and the GTA,
        we'll walk you through the real cost breakdown. no fluff, no upsells.
      </p>

      <h3>The four real ceramic coating tiers (and what they cost)</h3>
      <ul>
        <li>
          <strong>$0-$200. "Spray-on ceramic" / paste wax / hybrid sealants.</strong>{" "}
          These last 3-6 months at best. They are not true ceramic coatings.
          Most "$199 ceramic specials" advertised in Mississauga fall here.
          Avoid.
        </li>
        <li>
          <strong>$300-$500. Single-layer entry-level ceramic.</strong>{" "}
          Real SiO₂-based coatings, but typically applied with minimal prep
          and a 1-2 year warranty. Decent for daily drivers if you maintain
          them religiously.
        </li>
        <li>
          <strong>$550-$1,500. Hand-applied 2-layer Gtechniq ceramic with paint correction.</strong>{" "}
          This is the tier we operate in. Prep is the bulk of the labour:
          decontamination, claying, IPA wipedown, 1-stage paint correction,
          then layer 1 (Crystal Serum Light) cures, layer 2 (EXO v5) goes on.
          Comes with a written 5-year warranty.
        </li>
        <li>
          <strong>$1,800-$3,000+. Multi-stage correction + graphene-enhanced ceramic.</strong>{" "}
          For severely neglected paint or exotic vehicles requiring 2-stage or
          3-stage correction before the coating. Typically 7-9 year warranties.
        </li>
      </ul>

      <h3>Where most "ceramic coating Mississauga" pricing goes wrong</h3>
      <p>
        Cheap shops cut three corners: <strong>(1)</strong> they skip paint
        correction entirely (so the coating locks in your swirl marks
        permanently), <strong>(2)</strong> they apply a single thin layer (real
        protection needs two), and <strong>(3)</strong> they have no
        climate-controlled bay (so dust contaminates the coating during cure).
        The result is a coating that looks fine for two months and then starts
        peeling, water-spotting and losing hydrophobics.
      </p>
      <p>
        At Jester Werks our flagship 2-layer Mississauga package is currently{" "}
        <Link to="/ceramic-coating-mississauga" className="text-acid underline">
          $550 as a content-based offer
        </Link>{" "}
        (regularly $1,500). That includes full prep, 1-stage paint correction,
        the 2-layer ceramic system, a free window coating, a written 5-year
        warranty and a documented before/after report. If you're seeing prices
        meaningfully cheaper than that for a "real ceramic coating in
        Mississauga," ask three questions: is paint correction included? How
        many layers? Is there a written warranty?
      </p>
      <h3>What you should actually budget</h3>
      <p>
        For most daily drivers in Mississauga (sedans, SUVs, mid-size cars),
        budget <strong>$550-$900</strong> for a real 2-layer ceramic with
        paint correction and a 5-year warranty. For larger SUVs, full-size
        trucks or exotic finishes, expect $1,000+. Anything lower is almost
        always cutting one of the three corners above.
      </p>
    </>
  ),

  "best-ceramic-coating-mississauga": (
    <>
      <p>
        Searching for the <strong>best ceramic coating in Mississauga</strong>{" "}
        is harder than it should be. Every shop claims to be "the best."
        Here's how to actually evaluate them. written by the team that runs
        Mississauga's <em>most-reviewed</em> ceramic coating studio
        (<Link to="/reviews" className="text-acid underline">250+ verified
        Google reviews</Link>).
      </p>

      <h3>The 8 red flags that separate real ceramic shops from cheap scams</h3>
      <ol>
        <li>
          <strong>No paint depth measurement.</strong> A real shop measures
          your clear coat with a digital gauge before any correction. Ask to
          see the readings. If they don't have a gauge, walk away.
        </li>
        <li>
          <strong>No before/after photos under controlled lighting.</strong>{" "}
          Swirl-finder LEDs reveal defects sunlight can't show. If the shop
          can't show you swirl-finder photos of your car, they're guessing.
        </li>
        <li>
          <strong>No climate-controlled, dust-filtered bay.</strong> Coatings
          flash off in 60 seconds. Dust during cure = permanent imperfections.
          A real ceramic coating shop in Mississauga has a dedicated coating
          bay, not an open driveway.
        </li>
        <li>
          <strong>"All-in-one" sub-1-hour application.</strong> A proper
          decon + 1-stage correction + 2-layer coating takes 12-20 labour
          hours. Anyone offering "drop-off in the morning, pick up at 5"
          ceramic is cutting corners.
        </li>
        <li>
          <strong>No written warranty.</strong> Verbal warranties mean
          nothing. Ask for the warranty document up front. what it covers,
          what voids it, and how to claim.
        </li>
        <li>
          <strong>Subcontractors.</strong> Ask: "is the coating applied by
          you or a subcontractor?" Owner-operated shops put their reputation
          on every job.
        </li>
        <li>
          <strong>Single-layer-only.</strong> Real protection needs a hard
          base + a hydrophobic top coat. One layer doesn't deliver both.
        </li>
        <li>
          <strong>Sub-50 reviews.</strong> Reputation in this industry comes
          from years of consistent work. A shop with 12 reviews has not
          coated enough cars to know what they're doing.
        </li>
      </ol>

      <h3>The questions to ask any Mississauga ceramic coating shop</h3>
      <ul>
        <li>"How many layers do you apply?"</li>
        <li>"Do you measure paint depth before correction?"</li>
        <li>"Can I see before/after photos under swirl-finder lighting?"</li>
        <li>"Is the coating cured in a climate-controlled bay?"</li>
        <li>"Can I see the written warranty document?"</li>
        <li>"How many years has the same person been applying coatings?"</li>
      </ul>

      <h3>Why Jester Werks ranks #1 on Google for Mississauga ceramic coating</h3>
      <p>
        We are owner-operated, hand-finish every car, measure paint depth on
        every job, document the entire process, and back every coating with a
        written 5-year warranty.{" "}
        <Link to="/ceramic-coating-mississauga" className="text-acid underline">
          Our $550 content-based offer
        </Link>{" "}
        (regularly $1,500) is the easiest way to experience the difference.
        and there are still spots open for select Mississauga vehicles we
        feature on our channels.
      </p>
    </>
  ),

  "ceramic-coating-vs-ppf": (
    <>
      <p>
        The most common question we get from Mississauga drivers planning a
        new-car protection setup: <strong>ceramic coating or paint protection
        film (PPF)?</strong> Short answer. they do different jobs, and the
        smart play is often both.
      </p>

      <h3>What ceramic coating does well</h3>
      <ul>
        <li>5+ years of UV / chemical / oxidation protection</li>
        <li>Hydrophobic. water and dirt slide off</li>
        <li>Mirror-deep gloss enhancement</li>
        <li>Easier weekly washes (no clay, no wax)</li>
        <li>Affordable. $550-$1,500 for a full vehicle</li>
      </ul>

      <h3>What PPF does well</h3>
      <ul>
        <li>Physically stops rock chips, sand and minor impacts</li>
        <li>Self-healing top layer (heat removes light scratches)</li>
        <li>10+ year durability on premium films</li>
        <li>Best for high-impact panels. front bumper, hood, fenders, mirrors</li>
      </ul>

      <h3>Where each one falls short</h3>
      <p>
        <strong>Ceramic coating</strong> is a sacrificial chemical shield.
        it cannot stop a rock chip or a parking-lot ding. It will not save
        your hood from highway debris.
      </p>
      <p>
        <strong>PPF</strong> is expensive ($1,800+ for a front-end package,
        $5,000+ for a full vehicle) and the matte finishes can yellow over
        time on lower-grade films. PPF also doesn't have the same hydrophobic
        gloss as ceramic.
      </p>

      <h3>The Mississauga driver's smart play</h3>
      <p>
        For most enthusiasts, the answer is <strong>PPF the high-impact
        front panels (bumper, hood, fenders, mirrors)</strong> and{" "}
        <strong>ceramic coat the rest of the car</strong>. You get rock-chip
        protection where you actually need it, and 5+ years of glossy,
        easy-clean ceramic protection on every other panel. all without
        paying for full-vehicle PPF.
      </p>
      <p>
        At Jester Werks we focus on world-class ceramic coatings. If PPF is
        also on your shortlist, we partner with vetted Mississauga PPF
        installers and can coordinate the timing so the ceramic goes on top
        of the film (yes. ceramic on PPF is a great combination and
        manufacturer-approved).
      </p>
      <p>
        Either way, get a free written quote in 24 hours. Call (647) 646-7223 or{" "}
        <Link to="/ceramic-coating-mississauga#contact" className="text-acid underline">
          book online
        </Link>{" "}
        and we'll give you an honest, written recommendation.
      </p>
    </>
  ),
};

function articleSchema(post, url) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.description,
    image: post.cover,
    datePublished: post.date,
    dateModified: post.date,
    author: { "@type": "Organization", name: BRAND.name },
    publisher: {
      "@type": "Organization",
      name: BRAND.name,
      logo: {
        "@type": "ImageObject",
        url:
          "https://customer-assets.emergentagent.com/job_paint-pro-finish/artifacts/s2b2b55t_pup%20face%20joker-01-01%20%282%29.png",
      },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
  };
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  if (!post) {
    return (
      <div className="min-h-screen bg-jester-deep text-white flex items-center justify-center">
        <div className="text-center">
          <SEO
            title="Article Not Found | Jester Werks Blog"
            description="The article you're looking for could not be found."
            noindex
          />
          <h1 className="font-display text-4xl mb-4">Article not found</h1>
          <Link to="/blog" className="text-acid underline">
            ← Back to all articles
          </Link>
        </div>
      </div>
    );
  }
  const url = `${BRAND.url}/blog/${post.slug}`;
  return (
    <div className="grain bg-jester-deep text-white">
      <SEO
        title={post.title}
        description={post.description}
        canonical={url}
        image={post.cover}
        schemas={[
          localBusinessSchema(),
          articleSchema(post, url),
          breadcrumbSchema([
            { name: "Home", url: `${BRAND.url}/` },
            { name: "Blog", url: `${BRAND.url}/blog` },
            { name: post.title, url },
          ]),
        ]}
      />
      <Navbar />

      <article className="relative pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <Link
            to="/blog"
            data-testid="blog-back-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-acid font-label hover:text-white transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> All articles
          </Link>

          <div className="mt-8 flex flex-wrap items-center gap-4 text-xs text-white/50 uppercase tracking-[0.2em] font-label">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              {new Date(post.date).toLocaleDateString("en-CA", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              {post.readMin} min read
            </span>
          </div>

          <h1 className="mt-5 font-display text-4xl md:text-6xl leading-[1] tracking-tight">
            {post.title}
          </h1>

          <p className="mt-6 text-lg text-white/70 leading-relaxed">
            {post.description}
          </p>

          <div className="mt-8 rounded-2xl overflow-hidden border border-white/10">
            <img
              src={post.cover}
              alt={post.coverAlt || post.title}
              className="w-full h-auto object-cover"
              loading="eager"
              fetchpriority="high"
              decoding="async"
            />
          </div>

          <div className="mt-10 rounded-xl border border-acid/30 bg-acid/5 p-5 text-sm text-white/85">
            <div className="text-[10px] uppercase tracking-[0.3em] text-acid font-label mb-2">
              TL;DR
            </div>
            {post.tldr}
          </div>

          <div
            data-testid="blog-article-body"
            className="prose-jester mt-10 text-white/80 leading-relaxed text-[17px] space-y-5
              [&_h3]:font-display [&_h3]:text-2xl [&_h3]:mt-10 [&_h3]:mb-3 [&_h3]:text-white
              [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_ul]:my-4
              [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-2 [&_ol]:my-4
              [&_strong]:text-white [&_em]:text-white/90"
          >
            {BODIES[post.slug] || (
              <p>Article content coming soon.</p>
            )}
          </div>

          <div className="mt-14 rounded-2xl border border-acid/30 bg-gradient-to-br from-jester-purple/15 to-[#120127] p-7 md:p-9">
            <div className="text-[10px] uppercase tracking-[0.3em] text-acid font-label mb-2">
              Ready to book?
            </div>
            <h3 className="font-display text-2xl md:text-3xl">
              See our flagship Mississauga ceramic coating package
            </h3>
            <p className="mt-3 text-white/70">
              Limited content-based offer at $550 (regularly $1,500). Includes
              paint correction, 2-layer Gtechniq ceramic, free window coating and a
              written 5-year warranty.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/ceramic-coating-mississauga"
                data-testid="blog-cta-mississauga"
                className="inline-flex items-center gap-2 px-6 h-12 rounded-full bg-acid hover:bg-[#bff020] text-[#120127] font-label tracking-[0.2em] text-sm uppercase transition-colors"
              >
                Mississauga Package
              </Link>
              <Link
                to="/ceramic-coating-vaughan"
                data-testid="blog-cta-vaughan"
                className="inline-flex items-center gap-2 px-6 h-12 rounded-full border border-white/15 text-white font-label tracking-[0.2em] text-sm uppercase hover:border-acid hover:text-acid transition-colors"
              >
                Vaughan Package
              </Link>
            </div>
          </div>
        </div>
      </article>

      <CuratedReviews />
      <Contact />
      <Footer />
    </div>
  );
}
